/* eslint-disable no-restricted-globals, no-unused-vars */
// ─────────────────────────────────────────────────────────────────────────
// SERGA // PERSONAL OS — FEED tray (right rail)
// Two stacked sections inside one collapsible rail:
//   • FOCUS  — the agent-curated Today's Focus board (FOCUS in data.js, fed by
//              /feed → feed/focus.json). Self-contained items, optional back-link
//              to a project node. Per-item dismiss persists, keyed to FOCUS.rev,
//              so the agent's next push (new rev) re-seeds a fresh board.
//   • STREAM — the Mastodon home timeline (STREAM in data.js, fed by /feed →
//              the TTL-cached Mastodon fetch). Latest ~10 posts from accounts the
//              operator follows. refresh() re-renders after SergaOS re-polls /feed
//              (which only happens while the rail is open).
// Open on login; collapses to an edge handle; auto-hides when you drill in.
// ─────────────────────────────────────────────────────────────────────────
import { FOCUS, STREAM } from './data.js';
import { gIcon } from './glyphs.js';
import { linkify, agoText, minsSince, postAgo } from './textFormat.js';
import { el, trackedListeners } from './dom.js';

const TRAY_KEY = 'serga-os-tray';
const FOCUS_KEY = 'serga-os-focus';

const TYPE_LABEL = { task: 'TASK', event: 'EVENT', email: 'EMAIL', note: 'NOTE' };

export function createTray(world) {
  const tray = document.getElementById('tray');
  if (!tray) return { open() {}, refresh() {}, dispose() {} };

  const { on, dispose: disposeListeners } = trackedListeners();

  // ── persisted collapse state ────────────────────────────────────────────
  // On touch / narrow screens the rail defaults to COLLAPSED so it never traps
  // canvas touches over the right half of the field on load (a stored
  // preference always wins). Desktop keeps the open-by-default behaviour.
  const SMALL = typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse), (max-width: 1024px)').matches;
  let collapsed = SMALL;
  try {
    const stored = JSON.parse(localStorage.getItem(TRAY_KEY) || '{}');
    if (typeof stored.collapsed === 'boolean') collapsed = stored.collapsed;
  } catch (e) {}
  const saveTray = () => { try { localStorage.setItem(TRAY_KEY, JSON.stringify({ collapsed })); } catch (e) {} };
  // body.feed-open drives the switch-tab relocation: when FEED is open, the
  // SONIC rail's resting handle moves to FEED's left edge (and vice-versa) so a
  // panel never sits on top of the opposite rail's handle (see styles.css).
  const applyCollapse = () => {
    tray.classList.toggle('collapsed', collapsed);
    document.body.classList.toggle('feed-open', !collapsed);
  };
  applyCollapse();

  // ── dismissed focus items (keyed to the agent's push id; reloaded on rev change)
  let dismissRev = null;
  let dismissed = new Set();
  function loadDismissed() {
    if (FOCUS.rev === dismissRev) return;
    dismissRev = FOCUS.rev;
    try { dismissed = new Set(JSON.parse(localStorage.getItem(FOCUS_KEY + ':' + dismissRev) || '[]')); }
    catch (e) { dismissed = new Set(); }
  }
  const saveDismissed = () => { try { localStorage.setItem(FOCUS_KEY + ':' + dismissRev, JSON.stringify([...dismissed])); } catch (e) {} };

  const focusList = tray.querySelector('#focus-list');
  const focusCount = tray.querySelector('#focus-count');
  const focusStamp = tray.querySelector('#focus-stamp');
  const threadsList = tray.querySelector('#threads-list');
  const threadsTag = tray.querySelector('#tsec-threads .sync-tag');
  const threadsHead = tray.querySelector('#tsec-threads .tsec-head');

  // ── FOCUS detail card (one shared popover, anchored beside the clicked row) ─
  const fcard = el('div', 'fcard');
  fcard.innerHTML = `
    <span class="fc-corner tl"></span><span class="fc-corner br"></span>
    <button class="fc-close" title="close (Esc)">×</button>
    <div class="fc-head">
      <span class="fc-icon"></span>
      <span class="fc-type"></span>
    </div>
    <div class="fc-title"></div>
    <div class="fc-meta"></div>
    <div class="fc-rule"></div>
    <div class="fc-desc"></div>
    <button class="fc-link">OPEN LINKED NODE <span class="fc-arrow">↗</span></button>`;
  document.body.appendChild(fcard);
  const fcEls = {
    icon:  fcard.querySelector('.fc-icon'),
    type:  fcard.querySelector('.fc-type'),
    title: fcard.querySelector('.fc-title'),
    meta:  fcard.querySelector('.fc-meta'),
    desc:  fcard.querySelector('.fc-desc'),
    link:  fcard.querySelector('.fc-link'),
  };
  let cardOpenId = null, cardAnchor = null;

  function positionCard() {
    if (!cardAnchor) return;
    const rowR  = cardAnchor.getBoundingClientRect();
    const trayR = tray.getBoundingClientRect();
    const cardR = fcard.getBoundingClientRect();
    const gap = 14, margin = 16;
    // prefer sitting to the LEFT of the rail, vertically centred on the row;
    // if there isn't room (narrow screen), centre it over the field instead of
    // jamming it against the rail, and drop the connector nub.
    let left = trayR.left - gap - cardR.width;
    const centered = left < margin;
    if (centered) left = Math.max(margin, (window.innerWidth - cardR.width) / 2);
    fcard.classList.toggle('centered', centered);
    let top = rowR.top + rowR.height / 2 - cardR.height / 2;
    top = Math.max(margin, Math.min(top, window.innerHeight - cardR.height - margin));
    fcard.style.left = left + 'px';
    fcard.style.top  = top + 'px';
    // point the connector nub at the row's vertical centre
    const nub = Math.max(12, Math.min(rowR.top + rowR.height / 2 - top, cardR.height - 12));
    fcard.style.setProperty('--nub', nub + 'px');
  }

  function closeCard() {
    if (!cardOpenId) return;
    fcard.classList.remove('open');
    if (cardAnchor) cardAnchor.classList.remove('active');
    cardOpenId = null; cardAnchor = null;
  }

  function openCard(it, row) {
    if (cardOpenId === it.id) { closeCard(); return; }  // toggle on the same row
    if (cardAnchor) cardAnchor.classList.remove('active');
    cardOpenId = it.id; cardAnchor = row;
    row.classList.add('active');
    fcard.classList.toggle('urgent', !!it.urgent);
    fcEls.icon.innerHTML = gIcon(it.type, 16);
    fcEls.type.textContent = TYPE_LABEL[it.type] || String(it.type).toUpperCase();
    fcEls.title.textContent = it.title;
    fcEls.meta.textContent = it.meta || '';
    fcEls.meta.style.display = it.meta ? '' : 'none';
    fcEls.desc.textContent = it.description || 'No further detail provided.';
    fcEls.link.style.display = it.src ? '' : 'none';
    fcEls.link.onclick = it.src ? () => { closeCard(); world.enterItemById('z-projects', 'p-' + it.src); } : null;
    fcard.classList.add('open');
    requestAnimationFrame(positionCard);
  }

  // SHELL parity: open a focus item's card by id (returns false if not on board)
  function openById(id) {
    const it = (FOCUS.items || []).find(x => x.id === id && !dismissed.has(x.id));
    if (!it) return false;
    const row = [...focusList.children].find(r => r.__focusId === id);
    if (!row) return false;
    if (cardOpenId === id) return true;     // already open — don't toggle shut
    openCard(it, row);
    return true;
  }
  // SHELL parity: dismiss a focus item by id (same as the row ×)
  function dismissById(id) {
    if (!(FOCUS.items || []).some(x => x.id === id)) return false;
    if (cardOpenId === id) closeCard();
    dismissed.add(id); saveDismissed();
    refreshFocus();
    return true;
  }

  on(fcard.querySelector('.fc-close'), 'click', closeCard);
  on(document, 'keydown', e => { if (e.key === 'Escape' && cardOpenId) { e.stopPropagation(); closeCard(); } });
  // close when clicking elsewhere (not the card, not a focus row)
  on(document, 'pointerdown', e => {
    if (!cardOpenId) return;
    if (fcard.contains(e.target) || (cardAnchor && cardAnchor.contains(e.target))) return;
    closeCard();
  });
  on(window, 'resize', () => { if (cardOpenId) positionCard(); });
  on(focusList, 'scroll', () => { if (cardOpenId) positionCard(); });

  // ── FOCUS render ──────────────────────────────────────────────────────────
  function refreshFocus() {
    closeCard();                 // the live poll may destroy the anchored row
    loadDismissed();
    const updMin = minsSince(FOCUS.updatedAt);
    const upd = updMin != null ? agoText(updMin) + ' ago' : (FOCUS.updatedAgo || '—');
    focusStamp.textContent = (FOCUS.period ? FOCUS.period + ' · ' : '') + 'UPD ' + upd;

    const live = (FOCUS.items || []).filter(i => !dismissed.has(i.id));
    focusCount.textContent = String(live.length).padStart(2, '0');
    focusList.innerHTML = '';
    if (!live.length) {
      focusList.appendChild(el('div', 'focus-empty', 'BOARD CLEAR — agent re-seeds on next push'));
      return;
    }
    live.forEach(it => {
      const row = el('div', 'frow open-card' + (it.urgent ? ' urgent' : '') + (it.src ? ' linked' : ''));
      row.__focusId = it.id;
      row.innerHTML = `
        <span class="ficon">${gIcon(it.type, 15)}</span>
        <span class="fbody">
          <span class="ftitle">${it.title}</span>
          <span class="fmeta"><span class="ftype">${TYPE_LABEL[it.type] || String(it.type).toUpperCase()}</span>${it.meta ? '<span class="fdot">·</span>' + it.meta : ''}</span>
        </span>
        <button class="fclose" title="dismiss">×</button>`;
      row.querySelector('.fclose').addEventListener('click', e => {
        e.stopPropagation();
        row.classList.add('leaving');
        dismissed.add(it.id); saveDismissed();
        setTimeout(refreshFocus, 220);
      });
      // open the detail card beside the row (all items, src-linked or not)
      row.addEventListener('click', () => openCard(it, row));
      focusList.appendChild(row);
    });
  }

  // ── STREAM render ──────────────────────────────────────────────────────────
  function refreshStream() {
    if (threadsTag) threadsTag.firstChild.textContent = STREAM.handle || '@stream';
    const items = [...(STREAM.items || [])].slice(0, 10);
    threadsList.innerHTML = '';
    if (!items.length) {
      threadsList.appendChild(el('div', 'focus-empty', 'STREAM QUIET'));
      return;
    }
    items.forEach(t => {
      const row = el('div', 'trow-th');
      const handle = t.author || STREAM.handle || '';
      const open = t.permalink && t.permalink !== '#'
        ? `<a class="th-open" href="${t.permalink}" target="_blank" rel="noreferrer">VIEW ↗</a>`
        : '<span class="th-open">VIEW ↗</span>';
      row.innerHTML = `
        <div class="th-top"><span class="th-handle">${handle}</span><span class="th-time">${postAgo(t)}</span></div>
        <div class="th-text">${linkify(t.text)}</div>
        <div class="th-stats"><span>◈ ${t.replies || 0}</span><span>♡ ${t.likes || 0}</span>${open}</div>`;
      threadsList.appendChild(row);
    });
  }

  // brief sync flash on the THREADS header (visual feedback when re-polled)
  let syncT = null;
  function flashSync() {
    if (!threadsHead) return;
    threadsHead.classList.add('syncing');
    clearTimeout(syncT);
    syncT = setTimeout(() => threadsHead.classList.remove('syncing'), 1100);
  }

  // ── collapse / expand ────────────────────────────────────────────────────
  // The FEED + SONIC rails share the right dock — opening one collapses the other
  // via a window 'serga:rail-open' event carrying which rail opened.
  const RAIL_EVENT = 'serga:rail-open';
  const setCollapsed = v => {
    collapsed = v; applyCollapse(); saveTray();
    if (!v) window.dispatchEvent(new CustomEvent(RAIL_EVENT, { detail: 'feed' }));
  };
  on(window, RAIL_EVENT, e => { if (e.detail !== 'feed' && !collapsed) setCollapsed(true); });
  on(tray.querySelector('#tray-min'), 'click', () => setCollapsed(true));
  on(tray.querySelector('#tray-handle'), 'click', () => setCollapsed(false));

  refreshFocus();
  refreshStream();

  return {
    open() { if (!SMALL) setCollapsed(false); }, // login/unlock — skip on touch so the rail doesn't pop over the field
    refresh() { refreshFocus(); refreshStream(); flashSync(); },  // after a /feed re-poll
    focusOpen: openById,                       // SHELL: open a focus item's detail card
    focusDismiss: dismissById,                 // SHELL: dismiss a focus item
    dispose() {
      clearTimeout(syncT);
      closeCard();
      document.body.classList.remove('feed-open');  // don't leak the switch-tab class on unmount
      disposeListeners();
      if (fcard.parentNode) fcard.parentNode.removeChild(fcard);
    },
  };
}
